/**
 * 1.4.5: 邀请成员弹窗组件
 * YUI 风格设计，紫色渐变头部
 * 支持邮箱邀请和角色选择
 * 1.4.5: 修改邀请流程 - 创建邀请记录并发送邀请邮件
 *        被邀请者需要通过邮件链接确认后才能成为成员
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next'; // 1.5.18: 获取当前语言
import { X, Mail, Shield, User, Loader2, AlertCircle, Check, Copy } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  createInvitationLink, 
  sendInvitationEmail, 
  getCurrentOrganization,
  type MemberRole 
} from '../services/organizationService';
import { supabase } from '../lib/supabase';
import { formatOrganizationName } from '../utils/organizationUtils'; // 1.5.1

interface InviteMemberModalProps {
  organizationId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function InviteMemberModal({
  organizationId,
  onClose,
  onSuccess,
}: InviteMemberModalProps) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<MemberRole>('member');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 1.4.5: 邀请成功状态
  const [inviteSent, setInviteSent] = useState(false);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [emailSendError, setEmailSendError] = useState<string | null>(null);

  // 1.4.5: 复制链接
  const handleCopyLink = async () => {
    if (!inviteLink) return;
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 验证邮箱
    if (!email.trim()) {
      setError(t('pleaseEnterEmail', '请输入邮箱地址'));
      return;
    }

    // 简单的邮箱格式验证
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError(t('invalidEmailFormat', '邮箱格式不正确'));
      return;
    }

    setIsLoading(true);
    setError(null);
    setEmailSendError(null);

    try {
      // 1.4.5: 1. 创建邀请链接
      const { result, error: apiError } = await createInvitationLink(
        organizationId,
        email.trim(),
        role,
        7,  // 有效期 7 天
        1   // 最多使用 1 次
      );

      if (apiError) {
        setError(apiError.message);
        return;
      }

      if (!result?.invite_token) {
        setError(t('createInviteFailed', '创建邀请失败'));
        return;
      }

      // 生成邀请链接
      const link = `${window.location.origin}/invite/${result.invite_token}`;
      setInviteLink(link);

      // 1.4.5: 2. 获取组织信息和当前用户邮箱
      const { organization } = await getCurrentOrganization();
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!organization || !user) {
        // 邀请链接已创建，但无法发送邮件
        setInviteSent(true);
        setEmailSendError(t('emailSendFailed', '邮件发送失败，请手动发送邀请链接'));
        return;
      }

      // 1.4.5: 3. 发送邀请邮件
      // 1.5.1: 使用翻译后的组织名称
      // 1.5.18: 传入当前语言，邮件内容根据语言显示
      const currentLocale = i18n.language || localStorage.getItem('i18nextLng') || 'zh';
      const { success: emailSent, error: emailError } = await sendInvitationEmail({
        email: email.trim(),
        organizationName: formatOrganizationName(organization.name, t),
        inviterEmail: user.email || '',
        role,
        inviteLink: link,
        locale: currentLocale,
      });

      setInviteSent(true);
      
      if (!emailSent) {
        // 邮件发送失败，但邀请链接已创建
        setEmailSendError(emailError?.message || t('emailSendFailed', '邮件发送失败，请手动发送邀请链接'));
      }
    } catch (err: any) {
      setError(err.message || t('inviteFailed', '邀请失败'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* 背景遮罩 */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/50"
          onClick={onClose}
        />

        {/* 弹窗内容 */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        >
          {/* 紫色渐变头部 */}
          <div className="bg-gradient-to-r from-primary to-purple-600 p-6 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">
                    {t('inviteMember', '邀请成员')}
                  </h2>
                  <p className="text-white/80 text-sm">
                    {t('inviteMemberDesc', '向团队成员发送邀请邮件')}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {!inviteSent ? (
            // 邀请表单
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              {/* 邮箱输入 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('emailAddress', '邮箱地址')}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="member@example.com"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
                />
              </div>

              {/* 角色选择 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  {t('selectRole', '成员角色')}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {/* Owner 选项 */}
                  <button
                    type="button"
                    onClick={() => setRole('owner')}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      role === 'owner'
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        role === 'owner' ? 'border-primary' : 'border-gray-300'
                      }`}>
                        {role === 'owner' && (
                          <div className="w-2 h-2 rounded-full bg-primary" />
                        )}
                      </div>
                      <Shield className={`w-4 h-4 ${role === 'owner' ? 'text-primary' : 'text-gray-400'}`} />
                      <span className={`font-medium ${role === 'owner' ? 'text-primary' : 'text-gray-700'}`}>
                        Owner
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">
                      {t('ownerDesc', '可管理成员')}
                    </p>
                  </button>

                  {/* Member 选项 */}
                  <button
                    type="button"
                    onClick={() => setRole('member')}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      role === 'member'
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        role === 'member' ? 'border-primary' : 'border-gray-300'
                      }`}>
                        {role === 'member' && (
                          <div className="w-2 h-2 rounded-full bg-primary" />
                        )}
                      </div>
                      <User className={`w-4 h-4 ${role === 'member' ? 'text-primary' : 'text-gray-400'}`} />
                      <span className={`font-medium ${role === 'member' ? 'text-primary' : 'text-gray-700'}`}>
                        Member
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">
                      {t('memberDesc', '普通成员')}
                    </p>
                  </button>
                </div>
              </div>

              {/* 提示信息 */}
              <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                <p className="text-sm text-purple-700">
                  💡 {t('inviteHint', '邀请邮件将发送至指定邮箱，有效期 7 天')}
                </p>
              </div>

              {/* 错误提示 */}
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              {/* 按钮区域 */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-6 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {t('cancel', '取消')}
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 px-6 py-2.5 text-white bg-gradient-to-r from-primary to-primary-dark rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('sending', '发送中...')}
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4" />
                      {t('sendInvite', '发送邀请')}
                    </>
                  )}
                </button>
              </div>
            </form>
          ) : (
            // 1.4.5: 邀请发送成功界面
            <div className="p-6 space-y-5">
              {/* 成功图标 */}
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {emailSendError 
                    ? t('inviteCreated', '邀请已创建') 
                    : t('inviteSent', '邀请已发送')}
                </h3>
                <p className="text-sm text-gray-500">
                  {emailSendError 
                    ? t('inviteCreatedDesc', '邀请链接已创建，请手动发送给对方')
                    : t('inviteSentDesc', '邀请邮件已发送至')} <span className="font-medium text-gray-700">{email}</span>
                </p>
              </div>

              {/* 邮件发送失败提示 */}
              {emailSendError && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-700 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    {emailSendError}
                  </p>
                </div>
              )}

              {/* 邀请链接（可复制） */}
              {inviteLink && (
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <p className="text-xs text-gray-500 mb-2">{t('inviteLink', '邀请链接')}</p>
                  <div className="flex items-center gap-3">
                    <input
                      type="text"
                      value={inviteLink}
                      readOnly
                      className="flex-1 bg-transparent text-sm text-gray-700 outline-none truncate"
                    />
                    <button
                      onClick={handleCopyLink}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        copied
                          ? 'bg-green-100 text-green-700'
                          : 'bg-primary text-white hover:opacity-90'
                      }`}
                    >
                      {copied ? (
                        <>
                          <Check className="w-4 h-4" />
                          {t('copied', '已复制')}
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          {t('copy', '复制')}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* 说明信息 */}
              <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                <p className="text-sm text-purple-700">
                  📧 {t('inviteNextSteps', '被邀请者收到邮件后，点击链接即可加入组织。链接有效期 7 天。')}
                </p>
              </div>

              {/* 完成按钮 */}
              <button
                onClick={() => onSuccess()}
                className="w-full px-6 py-2.5 text-white bg-gradient-to-r from-primary to-primary-dark rounded-lg hover:opacity-90 transition-opacity"
              >
                {t('done', '完成')}
              </button>
            </div>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
